import React, { useEffect, useState, useCallback } from 'react';
import api from '../../api/client';
import Button from '../../components/Button';
import Badge from '../../components/Badge';
import FormField, { Input, Textarea, Select } from '../../components/FormField';

const toInputDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  const pad = (n) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
};

const EMPTY_FORM = {
  name: '', description: '', price_per_person: '', date_time: '', paypal_link: '', active: false,
  order_deadline: '', locked: false, min_orders: '',
};

function fmt(d) {
  if (!d) return '–';
  return new Date(d).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function AdminCatering() {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [caterings, setCaterings] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [expandedOrders, setExpandedOrders] = useState(null);
  const [guestForm, setGuestForm] = useState({ guest_name: '', num_people: 1 });
  const [guestSaving, setGuestSaving] = useState(false);
  const [guestError, setGuestError] = useState('');

  useEffect(() => { api.get('/api/events/all').then((r) => setEvents(r.data)); }, []);

  const load = useCallback(() => {
    if (!selectedEvent) return;
    api.get(`/api/admin/events/${selectedEvent}/catering`).then((r) => setCaterings(r.data));
  }, [selectedEvent]);

  useEffect(() => { load(); setExpandedOrders(null); }, [load]);

  const resetForm = () => { setForm(EMPTY_FORM); setEditingId(null); setError(''); };

  const startEdit = (c) => {
    setForm({
      name: c.name,
      description: c.description || '',
      price_per_person: c.price_per_person,
      date_time: toInputDate(c.date_time),
      paypal_link: c.paypal_link || '',
      active: c.active,
      order_deadline: toInputDate(c.order_deadline),
      locked: c.locked,
      min_orders: c.min_orders ?? '',
    });
    setEditingId(c.id);
    setError('');
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    const payload = {
      ...form,
      price_per_person: parseFloat(form.price_per_person) || 0,
      order_deadline: form.order_deadline || null,
      min_orders: form.min_orders === '' ? null : parseInt(form.min_orders, 10),
    };
    try {
      if (editingId) {
        await api.put(`/api/admin/catering/${editingId}`, payload);
      } else {
        await api.post(`/api/admin/events/${selectedEvent}/catering`, payload);
      }
      resetForm();
      load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Speichern');
    } finally { setSaving(false); }
  };

  const toggleActive = async (c) => {
    await api.put(`/api/admin/catering/${c.id}`, { active: !c.active });
    load();
  };

  const toggleLocked = async (c) => {
    await api.put(`/api/admin/catering/${c.id}`, { locked: !c.locked });
    load();
  };

  const remove = async (c) => {
    if (!window.confirm(`Caterer "${c.name}" wirklich löschen? Alle Anmeldungen gehen verloren.`)) return;
    await api.delete(`/api/admin/catering/${c.id}`);
    if (editingId === c.id) resetForm();
    load();
  };

  const setOrderPaid = async (order, paid) => {
    await api.put(`/api/admin/catering/orders/${order.id}/paid`, { paid });
    load();
  };

  const addGuestOrder = async (e, cateringId) => {
    e.preventDefault();
    setGuestSaving(true); setGuestError('');
    try {
      await api.post(`/api/admin/catering/${cateringId}/orders/guest`, {
        guest_name: guestForm.guest_name,
        num_people: parseInt(guestForm.num_people, 10) || 1,
      });
      setGuestForm({ guest_name: '', num_people: 1 });
      load();
    } catch (err) {
      setGuestError(err.response?.data?.detail || 'Fehler beim Hinzufügen');
    } finally { setGuestSaving(false); }
  };

  const deleteOrder = async (order) => {
    if (!window.confirm(`Anmeldung von "${order.guest_name}" wirklich löschen?`)) return;
    await api.delete(`/api/admin/catering/orders/${order.id}`);
    load();
  };

  return (
    <div>
      <h2 className="page-title">Catering-Verwaltung</h2>

      <FormField label="Event auswählen" className="max-w-sm">
        <Select value={selectedEvent} onChange={(e) => { setSelectedEvent(e.target.value); resetForm(); }}>
          <option value="">– Event wählen –</option>
          {events.map((ev) => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
        </Select>
      </FormField>

      {selectedEvent && (
        <>
          <div className="card mb-5">
            <h3 className="section-title">{editingId ? 'Caterer bearbeiten' : 'Caterer hinzufügen'}</h3>
            <form onSubmit={submit} className="grid sm:grid-cols-2 gap-x-4">
              <FormField label="Name" required>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="z.B. Spanferkel Hoffmann" required />
              </FormField>
              <FormField label="Kosten p.P. (€)" required>
                <Input type="number" step="0.01" min="0" value={form.price_per_person}
                  onChange={(e) => setForm({ ...form, price_per_person: e.target.value })} required />
              </FormField>
              <FormField label="Datum & Uhrzeit" required>
                <Input type="datetime-local" value={form.date_time} onChange={(e) => setForm({ ...form, date_time: e.target.value })} required />
              </FormField>
              <FormField label="Anmeldeschluss" hint="Nach diesem Zeitpunkt wird die Anmeldung automatisch gesperrt (optional)">
                <Input type="datetime-local" value={form.order_deadline} onChange={(e) => setForm({ ...form, order_deadline: e.target.value })} />
              </FormField>
              <FormField label="Mindestanzahl Anmeldungen" hint="Für die Fortschrittsanzeige bei den Teilnehmern (optional)">
                <Input type="number" min="1" step="1" value={form.min_orders}
                  onChange={(e) => setForm({ ...form, min_orders: e.target.value })} />
              </FormField>
              <FormField label="PayPal-Link" className="sm:col-span-2">
                <Input value={form.paypal_link} onChange={(e) => setForm({ ...form, paypal_link: e.target.value })} placeholder="https://paypal.me/..." />
              </FormField>
              <FormField label="Beschreibung" className="sm:col-span-2">
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Was gibt es, Anmeldeschluss, Hinweise..." />
              </FormField>
              <div className="sm:col-span-2 flex items-center gap-2 mb-2">
                <input id="active" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-4 h-4 accent-ocean-500" />
                <label htmlFor="active" className="text-sm text-slate-700">Aktiv (für Teilnehmer sichtbar & buchbar)</label>
              </div>
              <div className="sm:col-span-2 flex items-center gap-2 mb-4">
                <input id="locked" type="checkbox" checked={form.locked} onChange={(e) => setForm({ ...form, locked: e.target.checked })} className="w-4 h-4 accent-ocean-500" />
                <label htmlFor="locked" className="text-sm text-slate-700">Anmeldung manuell gesperrt</label>
              </div>
              {error && <p className="text-xs text-red-600 mb-3 sm:col-span-2">{error}</p>}
              <div className="sm:col-span-2 flex gap-2">
                <Button type="submit" loading={saving}>{editingId ? 'Speichern' : 'Hinzufügen'}</Button>
                {editingId && <Button type="button" variant="ghost" onClick={resetForm}>Abbrechen</Button>}
              </div>
            </form>
          </div>

          {caterings.length === 0 ? (
            <div className="card text-center py-8 text-slate-400 text-sm">Noch kein Caterer für dieses Event angelegt.</div>
          ) : (
            <div className="space-y-3">
              {caterings.map((c) => {
                const paidCount = c.orders.filter((o) => o.paid).length;
                const totalPeople = c.orders.reduce((s, o) => s + o.num_people, 0);
                return (
                  <div key={c.id} className="card">
                    <div className="flex flex-wrap justify-between items-start gap-3">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="font-bold text-ocean-900">{c.name}</span>
                          <Badge status={c.active ? 'confirmed' : 'draft'} custom={c.active ? 'Aktiv' : 'Inaktiv'} />
                          {c.is_locked && <Badge status="blocked" custom={c.locked ? 'Manuell gesperrt' : 'Anmeldeschluss erreicht'} />}
                        </div>
                        <p className="text-xs text-slate-500">
                          {fmt(c.date_time)} · {parseFloat(c.price_per_person).toFixed(2)} € p.P.
                        </p>
                        {c.order_deadline && (
                          <p className="text-xs text-slate-500 mt-1">Anmeldeschluss: {fmt(c.order_deadline)}</p>
                        )}
                        <p className="text-xs text-slate-500 mt-1">
                          {c.orders.length} Anmeldung{c.orders.length !== 1 ? 'en' : ''} · {totalPeople} Person{totalPeople !== 1 ? 'en' : ''} · {paidCount} bezahlt
                          {c.min_orders ? ` · Ziel: ${c.min_orders} bezahlte Anmeldungen (${Math.min(100, Math.round((paidCount / c.min_orders) * 100))}%)` : ''}
                        </p>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <Button variant="ghost" size="sm" onClick={() => toggleActive(c)}>{c.active ? 'Deaktivieren' : 'Aktivieren'}</Button>
                        <Button variant="ghost" size="sm" onClick={() => toggleLocked(c)}>{c.locked ? 'Entsperren' : 'Sperren'}</Button>
                        <Button variant="secondary" size="sm" onClick={() => startEdit(c)}>Bearbeiten</Button>
                        <Button variant="ghost" size="sm" onClick={() => {
                          setExpandedOrders(expandedOrders === c.id ? null : c.id);
                          setGuestForm({ guest_name: '', num_people: 1 });
                          setGuestError('');
                        }}>
                          {expandedOrders === c.id ? 'Anmeldungen ausblenden' : 'Anmeldungen anzeigen'}
                        </Button>
                        <Button variant="danger" size="sm" onClick={() => remove(c)}>Löschen</Button>
                      </div>
                    </div>

                    {expandedOrders === c.id && (
                      <div className="border-t border-slate-100 mt-3 pt-3">
                        {c.orders.length === 0 ? (
                          <p className="text-xs text-slate-400 mb-3">Noch keine Anmeldungen.</p>
                        ) : (
                          <table className="w-full text-sm mb-3">
                            <thead>
                              <tr className="text-xs text-slate-400 uppercase tracking-wider border-b border-slate-100">
                                <th className="text-left py-1.5 pr-4 font-medium">Teilnehmer</th>
                                <th className="text-left py-1.5 pr-4 font-medium">Personen</th>
                                <th className="text-left py-1.5 pr-4 font-medium">Gesamt</th>
                                <th className="text-left py-1.5 pr-4 font-medium">Status</th>
                                <th className="text-left py-1.5 font-medium"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {c.orders.map((o) => (
                                <tr key={o.id} className="border-b border-slate-50 last:border-0">
                                  <td className="py-2 pr-4 font-medium text-slate-800">
                                    {o.user ? `${o.user.firstname} ${o.user.lastname}` : (o.guest_name || `#${o.id}`)}
                                    {!o.user_id && <span className="ml-1.5 text-xs font-normal px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">manuell</span>}
                                  </td>
                                  <td className="py-2 pr-4">{o.num_people}</td>
                                  <td className="py-2 pr-4 font-semibold text-sand-700">{(parseFloat(c.price_per_person) * o.num_people).toFixed(2)} €</td>
                                  <td className="py-2 pr-4">
                                    <button onClick={() => setOrderPaid(o, !o.paid)}
                                      className="cursor-pointer border-0 bg-transparent p-0">
                                      {o.paid
                                        ? <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">✓ Bezahlt</span>
                                        : <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">Als bezahlt markieren</span>
                                      }
                                    </button>
                                  </td>
                                  <td className="py-2">
                                    {!o.user_id && (
                                      <button onClick={() => deleteOrder(o)} className="cursor-pointer border-0 bg-transparent p-0 text-xs text-red-600 hover:underline">
                                        Entfernen
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}

                        <form onSubmit={(e) => addGuestOrder(e, c.id)} className="flex flex-wrap items-end gap-2">
                          <FormField label="Person manuell hinzufügen" className="mb-0 grow min-w-[160px]">
                            <Input value={guestForm.guest_name} placeholder="Name"
                              onChange={(e) => setGuestForm({ ...guestForm, guest_name: e.target.value })} />
                          </FormField>
                          <FormField label="Personen" className="mb-0 w-24">
                            <Input type="number" min="1" step="1" value={guestForm.num_people}
                              onChange={(e) => setGuestForm({ ...guestForm, num_people: e.target.value })} />
                          </FormField>
                          <Button type="submit" size="sm" loading={guestSaving} disabled={!guestForm.guest_name.trim()}>Hinzufügen</Button>
                        </form>
                        {guestError && <p className="text-xs text-red-600 mt-2">{guestError}</p>}
                        <p className="text-xs text-slate-400 mt-1.5">Für Personen ohne Account/Ticket, z.B. Gäste. Bezahlt-Status kann danach in der Tabelle gesetzt werden.</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
